import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { BrandProvider, useBrands, useActiveBrand, useApplyBrandTheme } from "@/context/BrandContext";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

import BrandSelector from "@/pages/BrandSelector";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Shop from "@/pages/Shop";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import MyOrders from "@/pages/MyOrders";
import OrderDetail from "@/pages/OrderDetail";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import Scanner from "@/pages/Scanner";

import "@/App.css";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-[#8C8581]">Loading…</div>;
  if (!user) {
    // redirect to brand-scoped login when inside /b/:brand, otherwise admin-login
    return <Navigate to="/" replace />;
  }
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function ProtectedAdminOrStaff({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-[#8C8581]">Loading…</div>;
  if (!user) return <Navigate to="/admin-login" replace />;
  if (user.role !== "admin" && user.role !== "staff") return <Navigate to="/" replace />;
  return children;
}

function BrandGate() {
  const brand = useActiveBrand();
  const { brand: key } = useParams();
  const { brands, loading } = useBrands();
  useApplyBrandTheme(brand);

  if (loading) return <div className="p-10 text-center text-[#8C8581]">Loading…</div>;
  if (!brand) {
    // unknown key
    if (brands.length > 0 && key) return <Navigate to="/" replace />;
    return null;
  }
  return (
    <div className="min-h-screen flex flex-col" data-brand={brand.key}
      style={{ background: brand.key === "blackpekoe" ? "#0F1411" : "#FDFBF7" }}
    >
      <Header brand={brand} />
      <main className="flex-1"><Outlet context={{ brand }} /></main>
      <Footer brand={brand} />
    </div>
  );
}

function AdminLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <BrandProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              {/* Brand selector landing */}
              <Route path="/" element={<BrandSelector />} />

              {/* Admin auth (shared, parent-level) */}
              <Route element={<AdminLayout />}>
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedAdminOrStaff><Admin /></ProtectedAdminOrStaff>} />
                <Route path="/admin/scan" element={<ProtectedAdminOrStaff><Scanner defaultAction="packed" /></ProtectedAdminOrStaff>} />
              </Route>

              {/* Brand-scoped routes */}
              <Route path="/b/:brand" element={<BrandGate />}>
                <Route index element={<Shop />} />
                <Route path="about" element={<Landing />} />
                <Route path="login" element={<Login />} />
                <Route path="signup" element={<Signup />} />
                <Route path="shop" element={<Shop />} />
                <Route path="cart" element={<Cart />} />
                <Route path="checkout" element={<Protected><Checkout /></Protected>} />
                <Route path="orders" element={<Protected><MyOrders /></Protected>} />
                <Route path="orders/:id" element={<Protected><OrderDetail /></Protected>} />
                <Route path="scan" element={<Protected><Scanner defaultAction="delivered" /></Protected>} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </BrandProvider>
  );
}
